/** @jsxImportSource @emotion/react */

import { ActiveSelection } from 'fabric/fabric-impl';
import { Group as GroupIcon, UngroupIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store/store';
import customization from 'modules/overlay-canvas/addons/customization';
import history from 'modules/overlay-canvas/addons/history';
import UDIconButton from 'ud-ui/components/icon-button';
import UDTippy from 'ud-ui/components/tippy';
import { setActiveObjects } from 'modules/template-editor/store/editor/editor';
import { selectActiveModal, selectActiveObjects, selectFabricManager } from 'modules/template-editor/store/editor/selectors';

export function Group() {
  const dispatch = useDispatch<AppDispatch>();
  const activeModal = useSelector(selectActiveModal);
  const manager = useSelector(selectFabricManager);
  const activeObjects = useSelector(selectActiveObjects);
  const isGroupedObjects = useMemo(() => {
    if (!manager) {
      return false;
    }
    return activeObjects.length === 1 && activeObjects[0].data?.type === 'group';
  }, [activeObjects, manager]);
  // const boardType = useGetBoardType();

  const toGroup = useCallback(() => {
    if (!manager) {
      return;
    }
    const activeObject = manager.canvas.getActiveObject();
    if (!activeObject) {
      return;
    }
    if (activeObject?.type !== 'activeSelection') {
      return;
    }

    const group = (activeObject as ActiveSelection).toGroup();
    group.data = { ...group.data, type: 'group' };
    group.set({ ...customization.getAllProperties() });
    manager.canvas.requestRenderAll();
    dispatch(setActiveObjects([group]));
  }, [dispatch, manager]);

  const toUngroup = useCallback(() => {
    if (!manager) {
      return;
    }
    const activeObject = manager.canvas.getActiveObject();
    if (!activeObject) {
      return;
    }

    const activeSelection = (activeObject as ActiveSelection).toActiveSelection();
    const objects = activeSelection.getObjects();
    objects.forEach((obj) => obj.set({ ...customization.getAllProperties() }));
    manager.canvas.requestRenderAll();
    dispatch(setActiveObjects(objects));
  }, [dispatch, manager]);

  const onClick = useCallback(() => {
    if (!manager) {
      return;
    }
    history.saveState();
    history.disableHistory();
    if (isGroupedObjects) {
      toUngroup();
    } else if (activeObjects.length > 1) {
      toGroup();
    }
    history.activeHistory();
  }, [activeObjects.length, isGroupedObjects, manager, toGroup, toUngroup]);

  const tooltipText = useMemo(() => {
    return isGroupedObjects ? 'Разгруппировать' : 'Группировать';
  }, [isGroupedObjects]);

  return (
    <UDTippy disabled={Boolean(activeModal)} content={tooltipText} size="xs">
      <span style={{ display: 'inline-block' }}>
        <UDIconButton
          variant="ghost"
          icon={isGroupedObjects ? <UngroupIcon size={20} strokeWidth={1.5} /> : <GroupIcon size={20} strokeWidth={1.5} />}
          onClick={onClick}
          disabled={activeObjects.length < 2 && !isGroupedObjects}
          tabIndex={0}
        />
      </span>
    </UDTippy>
  );
}
